import React from "react";
import { useTranslation } from "react-i18next";
import RefSiteFooter from "./RefSiteFooter";

const PRIVACY_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rp-ink: #163A5A; --rp-ink2: #3D6A8C; --rp-ink3: #5E87A3;
    --rp-sky: #1A63A5; --rp-mint: #2AB6E8;
    --rp-secondary: #2AB6E8;
    --rp-secondary-soft: #D9F4FC;
    --rp-secondary-muted: rgba(42,182,232,0.18);
    --rp-sky-light: #E8F4FC;
    --rp-white: #FFFFFF; --rp-surface: #F5F9FC;
    --rp-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rp-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rp-page { font-family: var(--rp-ff-body); color: var(--rp-ink); background: var(--rp-white); line-height: 1.6; }

  /* HERO */
  .rp-hero {
    text-align: center; padding: 54px 40px 0px;
    background: linear-gradient(180deg, #E8F4FC 0%, var(--rp-secondary-soft) 35%, #fff 100%);
    position: relative; overflow: hidden;
  }
  .rp-hero::before {
    content: ''; position: absolute; top: -150px; left: 50%; transform: translateX(-50%);
    width: 900px; height: 500px;
    background: radial-gradient(ellipse, rgba(26,99,165,.08) 0%, transparent 65%);
    pointer-events: none;
  }
  .rp-hero-content { position: relative; z-index: 1; }
  .rp-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, var(--rp-sky-light) 0%, var(--rp-secondary-soft) 100%);
    color: var(--rp-sky); border: 1px solid var(--rp-secondary-muted);
    font-size: 12px; font-weight: 600; padding: 8px 16px;
    border-radius: 100px; margin-bottom: 24px; letter-spacing: .06em;
    text-transform: uppercase;
    box-shadow: 0 4px 12px rgba(42,182,232,0.1);
  }
  .rp-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--rp-secondary); animation: rp-pulse 2s infinite; }
  @keyframes rp-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(0.8)} }
  .rp-h1 { font-family: var(--rp-ff-head); font-size: 60px; font-weight: 800; letter-spacing: -.02em; line-height: 1.05; margin-bottom: 20px; }
  .rp-h1-grad { background: linear-gradient(135deg, var(--rp-sky), var(--rp-mint)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .rp-last-updated { font-size: 14px; color: var(--rp-ink3); font-weight: 500; }

  /* CONTENT */
  .rp-content-wrap { padding: 48px 24px; max-width: 1100px; margin: 0 auto; }
  .rp-card {
    background: transparent; border-radius: 24px;
    padding: 60px; 
  }
  
  .rp-section { margin-bottom: 48px; }
  .rp-section:last-child { margin-bottom: 0; }
  
  .rp-h2 { font-family: var(--rp-ff-head); font-size: 28px; font-weight: 800; color: var(--rp-ink); margin-bottom: 20px; letter-spacing: -.01em; }
  .rp-p { font-size: 17px; color: var(--rp-ink2); margin-bottom: 24px; line-height: 1.75; }
  
  .rp-list { list-style: none; padding: 0; margin: 0 0 24px 0; }
  .rp-list-item { 
    position: relative; padding-left: 28px; margin-bottom: 16px; font-size: 17px; color: var(--rp-ink2); 
    display: flex; flex-direction: column;
  }
  .rp-list-item::before {
    content: '→'; position: absolute; left: 0; color: var(--rp-mint); font-weight: 700;
  }
  .rp-list-label { font-weight: 700; color: var(--rp-ink); margin-bottom: 4px; }

  @media (max-width: 768px) {
    .rp-h1 { font-size: 44px; }
    .rp-card { padding: 40px 24px; }
    .rp-h2 { font-size: 24px; }
  }
`;

function RefPrivacy() {
  const { t } = useTranslation('privacy');

  return (
    <div className="rp-page">
      <style>{PRIVACY_STYLES}</style>

      {/* HERO */}
      <div className="rp-hero">
        <div className="rp-hero-content">
          <div className="rp-eyebrow">
            <span className="rp-eyebrow-dot" /> {t('hero.eyebrow')}
          </div>
          <h1 className="rp-h1">
            {t('hero.titlePrefix')}<span className="rp-h1-grad">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="rp-last-updated">{t('hero.lastUpdated')}</p>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="rp-content-wrap">
        <div className="rp-card">
          <div className="rp-section">
            <p className="rp-p">
              {t('intro')}
            </p>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('collect.title')}</h2>
            <div className="rp-list">
              <div className="rp-list-item">
                <span className="rp-list-label">{t('collect.personalLabel')}</span>
                {t('collect.personalDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('collect.documentLabel')}</span>
                {t('collect.documentDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('collect.usageLabel')}</span>
                {t('collect.usageDesc')}
              </div>
            </div>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('use.title')}</h2>
            <div className="rp-list">
              <div className="rp-list-item">
                <span className="rp-list-label">{t('use.provisionLabel')}</span>
                {t('use.provisionDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('use.commLabel')}</span>
                {t('use.commDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('use.improveLabel')}</span>
                {t('use.improveDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('use.securityLabel')}</span>
                {t('use.securityDesc')}
              </div>
            </div>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('share.title')}</h2>
            <div className="rp-list">
              <div className="rp-list-item">
                <span className="rp-list-label">{t('share.consentLabel')}</span>
                {t('share.consentDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('share.providersLabel')}</span>
                {t('share.providersDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('share.complianceLabel')}</span>
                {t('share.complianceDesc')}
              </div>
            </div>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('security.title')}</h2>
            <p className="rp-p">
              {t('security.p1')}
            </p>
            <p className="rp-p">
              {t('security.p2')}
            </p>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('rights.title')}</h2>
            <div className="rp-list">
              <div className="rp-list-item">
                <span className="rp-list-label">{t('rights.accessLabel')}</span>
                {t('rights.accessDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('rights.deleteLabel')}</span>
                {t('rights.deleteDesc')}
              </div>
              <div className="rp-list-item">
                <span className="rp-list-label">{t('rights.optOutLabel')}</span>
                {t('rights.optOutDesc')}
              </div>
            </div>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('cookies.title')}</h2>
            <p className="rp-p">
              {t('cookies.p')}
            </p>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('transfers.title')}</h2>
            <p className="rp-p">
              {t('transfers.p')}
            </p>
          </div>

          <div className="rp-section">
            <h2 className="rp-h2">{t('contact.title')}</h2>
            <p className="rp-p">
              {t('contact.pPrefix')}<strong>{t('contact.email')}</strong>{t('contact.pSuffix')}
            </p>
          </div>
        </div>
      </div>

      <RefSiteFooter />
    </div>
  );
}

export default RefPrivacy;
